import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatApiErrorDetail } from '../lib/utils';
import { ArrowLeft, Mail, Lock, User, UserPlus, Eye, EyeOff } from 'lucide-react';
import { startGoogleLogin } from '../lib/googleAuth';
import PasswordStrengthMeter, { evaluatePassword } from '../components/PasswordStrengthMeter';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const qp = new URLSearchParams(window.location.search);
  const qn = (qp.get('name') || '').trim().split(' ');
  const [formData, setFormData] = useState({ name: '', first_name: qn[0] || '', last_name: qn.slice(1).join(' '), phone: '', email: qp.get('email') || '', password: '', confirmPassword: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password) {
      setError('Please fill in first name, last name, email, and password');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const { isStrong } = evaluatePassword(formData.password);
    if (!isStrong) {
      setError('Please choose a stronger password (meet at least 4 of 5 criteria below).');
      return;
    }

    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy.');
      return;
    }

    setLoading(true);
    try {
      const fullName = `${formData.first_name.trim()} ${formData.last_name.trim()}`.trim() || formData.name.trim();
      const u = await register(fullName, formData.email.trim(), formData.password, true, {
        first_name: formData.first_name.trim() || null,
        last_name: formData.last_name.trim() || null,
        phone: formData.phone.trim() || null,
      });
      toast.success(u.existing_account
        ? `Welcome back, ${u.name?.split(' ')[0] || 'friend'}! You already have an account, so we signed you in.`
        : `Welcome to DCA Enterprises, ${u.name?.split(' ')[0] || 'friend'}! Your account is ready.`);
      if (localStorage.getItem('studio_subscribe_tier') && u.role !== 'admin' && u.role !== 'super_admin') {
        navigate('/studio/start', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin('main', '/');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="register-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] py-4">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="DCA Enterprises" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/" 
              className="text-sm text-[#475569] hover:text-[#1D4ED8] flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Website
            </Link>
          </div>
        </div>
      </header>

      {/* Register Form */}
      <div className="flex-grow flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 border border-[#E2E8F0] shadow-sm">
            <div className="text-center mb-8">
              <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-2" data-testid="register-title">
                Create Account
              </h1>
              <p className="text-[#475569] text-sm">
                Join DCA Enterprises to get started
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="register-first-name" className="block text-sm font-medium text-[#0A0F1C] mb-2">First Name <span className="text-red-500" aria-hidden="true">*</span></label>
                  <div className="relative">
                    <Input
                      id="register-first-name"
                      type="text"
                      placeholder="Jane"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="form-input pl-10"
                      data-testid="register-first-name-input"
                      required
                      autoComplete="given-name"
                    />
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  </div>
                </div>
                <div>
                  <label htmlFor="register-last-name" className="block text-sm font-medium text-[#0A0F1C] mb-2">Last Name <span className="text-red-500" aria-hidden="true">*</span></label>
                  <Input
                    id="register-last-name"
                    type="text"
                    placeholder="Doe"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="form-input"
                    data-testid="register-last-name-input"
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="register-phone" className="block text-sm font-medium text-[#0A0F1C] mb-2">Phone <span className="text-[#94A3B8] text-xs ml-1">(optional)</span></label>
                <Input
                  id="register-phone"
                  type="tel"
                  placeholder="+1 555 555 0100"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="form-input"
                  data-testid="register-phone-input"
                  autoComplete="tel"
                />
              </div>

              <div>
                <label htmlFor="register-email" className="block text-sm font-medium text-[#0A0F1C] mb-2">Email <span className="text-red-500" aria-hidden="true">*</span></label>
                <div className="relative">
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="form-input pl-10"
                    data-testid="register-email-input"
                    required
                    autoComplete="email"
                    aria-required="true"
                  />
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                </div>
              </div>
              
              <div>
                <label htmlFor="register-password" className="block text-sm font-medium text-[#0A0F1C] mb-2">Password <span className="text-red-500" aria-hidden="true">*</span></label>
                <div className="relative">
                  <Input
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="form-input pl-10 pr-10"
                    data-testid="register-password-input"
                    required
                    autoComplete="new-password"
                    aria-required="true"
                  />
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#1D4ED8]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="register-password-toggle"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <PasswordStrengthMeter password={formData.password} testId="register-password-strength" />
              </div>

              <div>
                <label htmlFor="register-confirm-password" className="block text-sm font-medium text-[#0A0F1C] mb-2">Confirm Password <span className="text-red-500" aria-hidden="true">*</span></label>
                <div className="relative">
                  <Input
                    id="register-confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="form-input pl-10 pr-10"
                    data-testid="register-confirm-password-input"
                    required
                    autoComplete="new-password"
                    aria-required="true"
                  />
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#1D4ED8]"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    data-testid="register-confirm-toggle"
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="register-terms-label">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-[#E2E8F0]"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  data-testid="register-terms-checkbox"
                />
                <span className="text-[#475569] leading-snug">
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#1D4ED8] hover:underline">Terms of Service</Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-[#1D4ED8] hover:underline">Privacy Policy</Link>.
                </span>
              </label>

              <Button 
                type="submit" 
                disabled={loading}
                className="btn-primary-blue w-full flex items-center justify-center gap-2"
                data-testid="register-submit-button"
              >
                {loading ? 'Creating Account...' : (
                  <>
                    <UserPlus size={18} />
                    Create Account
                  </>
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E2E8F0]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-[#94A3B8]">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-[#E2E8F0] bg-white hover:bg-[#F9FAFB] transition-colors text-[#0A0F1C] font-medium"
              data-testid="register-google-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </button>

            <p className="text-center text-sm text-[#475569] mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#1D4ED8] font-medium hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
